import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

const USER_REASONS = [
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'hate_speech', label: 'Hate speech' },
  { value: 'violence', label: 'Violence or threats' },
  { value: 'other', label: 'Other' }
];

const EVENT_REASONS = [
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'spam', label: 'Spam or scam' },
  { value: 'misleading', label: 'Misleading information' },
  { value: 'dangerous', label: 'Dangerous or illegal activity' },
  { value: 'harassment', label: 'Harassment or hate speech' },
  { value: 'fake_event', label: 'Fake or fraudulent event' },
  { value: 'other', label: 'Other' }
];

const ReportModal = () => {
  const [modal, setModal] = useState(null); // { reportType, reportId, reportName, reportEmail }
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleOpen = (e) => {
      setModal(e.detail);
      setReason('');
      setDescription('');
      setLoading(false);
    };
    window.addEventListener('openReportModal', handleOpen);
    return () => window.removeEventListener('openReportModal', handleOpen);
  }, []);

  if (!modal) return null;

  const { reportType, reportId, reportName, reportEmail = '' } = modal;
  const isUser = reportType === 'user';
  const reasons = isUser ? USER_REASONS : EVENT_REASONS;

  const descTrimmed = description.trim().replace(/\s+/g, '');
  const isValid = reason !== '' && descTrimmed.length >= 20;

  const handleClose = () => setModal(null);

  const handleSubmit = async () => {
    if (!isValid || loading) return;
    setLoading(true);

    try {
      const currentUser = window.currentAppUser || window.currentUser;
      if (!currentUser) {
        window.showToast('You must be logged in to report', 'error');
        setLoading(false);
        return;
      }

      const supabaseClient = window.supabase || supabase;

      const dbData = {
        reporter_id: currentUser.uid,
        reporter_name: currentUser.profile?.name || 'Unknown',
        reporter_email: currentUser.email || 'No email',
        reason,
        description: description || 'No additional details provided',
        type: isUser ? 'user' : 'event',
        status: 'pending',
        created_at: new Date().toISOString()
      };

      if (isUser) {
        dbData.reported_user_id = reportId;
        dbData.reported_user_name = reportName;
        dbData.reported_user_email = reportEmail || 'Not available';
      } else {
        dbData.reported_event_id = reportId;
        dbData.reported_event_title = reportName;
      }

      const { error: dbError } = await supabaseClient.from('reports').insert(dbData);
      if (dbError) throw new Error('Failed to save report');

      // Auto-hide event if 3+ pending reports
      if (!isUser) {
        const { count: pendingCount } = await supabaseClient
          .from('reports')
          .select('*', { count: 'exact', head: true })
          .eq('reported_event_id', reportId)
          .eq('type', 'event')
          .eq('status', 'pending');

        if (pendingCount >= 3) {
          await supabaseClient.from('events').update({ hidden: true }).eq('event_id', reportId);
        }
      }

      handleClose();
      window.showToast('Report submitted successfully. Thank you for helping keep our community safe.', 'success');
    } catch (err) {
      window.showToast('Failed to send report. Please try again.', 'error');
      setLoading(false);
    }
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10005,
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          animation: 'slideUp 0.3s ease-out'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px', height: '40px', background: '#fef7f7', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ea4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#202124', flex: 1 }}>
            Report {isUser ? reportName : `Event: ${reportName}`}
          </h3>
        </div>

        {/* Reason */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5f6368', marginBottom: '8px' }}>
            Reason *
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{
              width: '100%', padding: '12px',
              border: '2px solid #dadce0', borderRadius: '8px',
              fontSize: '14px', color: reason ? '#202124' : '#9ca3af',
              background: 'white', cursor: 'pointer', boxSizing: 'border-box'
            }}
          >
            <option value="">Select a reason</option>
            {reasons.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#5f6368', marginBottom: '8px' }}>
            Additional details * (minimum 20 characters)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please provide any additional information (minimum 20 characters)..."
            maxLength={500}
            style={{
              width: '100%', minHeight: '100px', padding: '12px',
              border: '2px solid #dadce0', borderRadius: '8px',
              fontSize: '14px', color: '#202124', fontFamily: 'inherit',
              resize: 'vertical', boxSizing: 'border-box'
            }}
          />
          <div style={{ fontSize: '11px', color: '#5f6368', textAlign: 'right', marginTop: '4px' }}>
            {descTrimmed.length}/500
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleSubmit}
            disabled={!isValid || loading}
            onMouseOver={(e) => { if (isValid && !loading) e.currentTarget.style.background = '#d33426'; }}
            onMouseOut={(e) => { if (isValid && !loading) e.currentTarget.style.background = '#ea4335'; }}
            style={{
              flex: 1, padding: '12px', background: '#ea4335', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600,
              cursor: isValid && !loading ? 'pointer' : 'not-allowed',
              opacity: isValid && !loading ? 1 : 0.5,
              transition: 'all 0.2s'
            }}
          >
            {loading ? 'Sending...' : 'Send Report'}
          </button>
          <button
            onClick={handleClose}
            disabled={loading}
            onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = '#f1f3f4'; }}
            onMouseOut={(e) => { if (!loading) e.currentTarget.style.background = '#f8f9fa'; }}
            style={{
              flex: 1, padding: '12px', background: '#f8f9fa', color: '#5f6368',
              border: '2px solid #dadce0', borderRadius: '8px', fontSize: '15px',
              fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1, transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default ReportModal;